import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import samhealthLogo from '../../assets/samhealth.png';

// Mirror KYP comprehensive styles and structure
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 15,
    fontFamily: 'Helvetica',
    fontSize: 12,
    lineHeight: 1.3,
  },
  // Header with logos (SamHealth left, Lead logo right)
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 15,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
  },
  logoBox: {
    width: 100,
    height: 56,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#000000',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 3,
    overflow: 'hidden',
  },
  logoText: { fontSize: 9, fontWeight: 'bold', textAlign: 'center' },
  // Title bar (blue)
  titleBar: {
    backgroundColor: '#1e3a8a',
    color: 'white',
    padding: 4,
    marginBottom: 4,
    fontSize: 14,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  // Section headers (dark blue - consistent with KYP)
  sectionHeader: {
    backgroundColor: '#1e3a8a',
    color: 'white',
    padding: 3,
    marginBottom: 1,
    fontSize: 11,
    fontWeight: 'bold',
  },
  // Two column layout
  twoColumn: { flexDirection: 'row', marginBottom: 3 },
  leftColumn: { width: '48%', marginRight: '2%' },
  rightColumn: { width: '50%' },
  // Field rows
  fieldRow: { flexDirection: 'row', marginBottom: 0.5, fontSize: 10 },
  fieldLabel: { width: '45%', fontSize: 10, color: '#000' },
  fieldValue: { width: '55%', fontSize: 10, fontWeight: 'bold' },
  // Blue section headers
  blueSectionHeader: {
    backgroundColor: '#1e3a8a',
    color: 'white',
    padding: 3,
    marginBottom: 1,
    fontSize: 11,
    fontWeight: 'bold',
  },
  // Content box
  contentBox: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#000000',
    padding: 3,
    marginBottom: 3,
  },
  // Table (KYP style)
  table: { width: '100%', borderWidth: 1, borderColor: '#000', borderStyle: 'solid', marginTop: 4 },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#dddddd',
    borderBottomStyle: 'solid',
    minHeight: 25,
  },
  tableColLabel: {
    width: '25%',
    backgroundColor: '#F5DEB3',
    padding: 8,
    fontSize: 9,
    fontWeight: 'bold',
    borderRightWidth: 1,
    borderRightColor: '#dddddd',
    borderRightStyle: 'solid',
    justifyContent: 'center',
  },
  tableColValue: {
    width: '25%',
    padding: 8,
    fontSize: 9,
    borderRightWidth: 1,
    borderRightColor: '#dddddd',
    borderRightStyle: 'solid',
    justifyContent: 'center',
  },
  tr: { flexDirection: 'row' },
  th: { flex: 1, padding: 4, fontSize: 9, fontWeight: 'bold', backgroundColor: '#F3F4F6', borderRightWidth: 1, borderRightColor: '#000', borderRightStyle: 'solid' },
  td: { flex: 1, padding: 4, fontSize: 9, borderRightWidth: 1, borderRightColor: '#000', borderRightStyle: 'solid' },
  tdRight: { textAlign: 'right' },
  // Footer
    footer: { position: 'absolute', bottom: 4, left: 20, right: 20, fontSize: 8, textAlign: 'center' },
  
  // Enhanced coverage section (from KYP)
  coverageSection: {
    backgroundColor: '#FFF8DC',
    borderWidth: 1,
    borderColor: '#1e3a8a',
    borderStyle: 'solid',
    padding: 12,
    marginVertical: 15,
  },
  coverageHeader: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#1e3a8a',
    marginBottom: 10,
    textAlign: 'center',
  },
  coverageItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
    paddingVertical: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#DDD',
    borderBottomStyle: 'solid',
  },
  coverageLabel: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#1e3a8a',
    flex: 1,
  },
  coverageValue: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#2F4F4F',
    width: 120,
    textAlign: 'right',
  },
  highlightBox: {
    borderWidth: 1.2,
    borderColor: '#1e3a8a',
    borderStyle: 'solid',
    borderRadius: 4,
    backgroundColor: '#F0F6FF',
    padding: 8,
    marginTop: 6,
    marginBottom: 6,
  },
  highlightRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 },
  highlightLabel: { fontSize: 9, color: '#1e3a8a', fontWeight: 'bold' },
  highlightValue: { fontSize: 10, color: '#111827', fontWeight: 'bold' },
  highlightPayable: { fontSize: 11, color: '#B91C1C', fontWeight: 'bold' },
});

// Helpers (mirroring KYP)
const safeRender = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'object') {
    if (value.description !== undefined && value.diagnosis !== undefined && value.duration !== undefined) {
      const parts = [];
      if (value.diagnosis) parts.push(`Diagnosis: ${value.diagnosis}`);
      if (value.description) parts.push(`Description: ${value.description}`);
      if (value.duration) parts.push(`Duration: ${value.duration}`);
      return parts.join(' | ');
    }
    if (value.diagnosisName && value.procedureName) {
      const parts = [];
      if (value.diagnosisName) parts.push(value.diagnosisName);
      if (value.procedureName) parts.push(value.procedureName);
      if (value.description) parts.push(value.description);
      return parts.join(' - ');
    }
    if (Array.isArray(value)) return value.map(item => safeRender(item)).filter(Boolean).join(', ');
    const stringValues = Object.values(value).filter(v => v && typeof v === 'string');
    if (stringValues.length > 0) return stringValues.join(' ');
    return fallback;
  }
  return String(value);
};

const formatDate = (date) => {
  if (!date) return '';
  const d = date?.toDate ? date.toDate() : new Date(date);
  return d.toLocaleDateString('en-GB');
};
const formatINR = (amount) => `Rs. ${(Number(amount) || 0).toLocaleString('en-IN')}`;

const fmtDate = (date) => {
  if (!date) return '-';
  const d = date?.toDate ? date.toDate() : new Date(date);
  return d.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Helper function to normalize and clean text (from KYP)
const normalizeText = (str) => {
  if (typeof str !== 'string') return str;
  let s = str.replace(/\s+/g, ' ').trim();
  const fullRep = s.match(/^(.+?)\1+$/);
  if (fullRep) s = fullRep[1].trim();
  return s;
};

// Helper function to aggregate remarks from multiple sources
const aggregateRemarks = (application, additionalRemarks = '') => {
  const remarksSources = [
    application?.workflow?.medicalVerification?.letterRemarks,
    application?.workflow?.medicalVerification?.clinicalNotes,
    application?.workflow?.medicalVerification?.internalRemarks,
    application?.workflow?.dischargeSettlement?.comments,
    additionalRemarks
  ];
  
  const validRemarks = remarksSources
    .filter(remark => remark && remark.toString().trim())
    .map(remark => remark.toString().trim());
    
  return validRemarks.length > 0 
    ? validRemarks.join('. ').replace(/\.\s*\./g, '.').trim()
    : 'Final approval granted based on medical verification and discharge review. All policy terms and conditions apply.';
};
const formatTreatments = (treatments) => {
  if (!treatments || !Array.isArray(treatments)) return 'Not specified';
  return treatments.map((t, i) => {
    const parts = [];
    if (t.diagnosisName) parts.push(t.diagnosisName);
    if (t.procedureName) parts.push(t.procedureName);
    if (t.description) parts.push(t.description);
    return `${i + 1}. ${parts.join(' - ')}`;
  }).join('; ');
};

const ComprehensiveFALDocument = ({ application, remarks = '', leadGeneratorData }) => {
  const app = application || {};
  const wf = app.workflow || {};
  const mv = wf.medicalVerification || {};
  const pv = wf.policyVerification || {};
  const ds = wf.dischargeSettlement || {};
  const lg = leadGeneratorData || app.leadGenerator || {};

  // Coverage breakdown (from policy verification like KYP)
  const coverage = pv.coverageBreakdown || {
    baseCoverage: Number(pv.policyDetails?.baseSumInsured || 0),
    cumulativeBonus: Number(pv.policyDetails?.cumulativeBonus || 0),
    topUpCoverage: Number(pv.policyDetails?.topUpSumInsured || 0),
    effectiveTotalSumInsured: Number(pv.policyDetails?.effectiveTotalSumInsured || 0),
    utilized: Number(pv.coverageBreakdown?.utilized || 0),
    balanceSumInsured: Number(pv.coverageBreakdown?.balanceSumInsured || 0),
  };

  // Room/ICU eligibility
  const roomEligibility = pv.roomEligibility || {};

  // Discharge items
  // Prefer items saved in medicalVerification.dischargeReview; if absent, fallback to
  // computed settlement breakdown in workflow.dischargeSettlement.itemsBreakdown
  const rawMVItems = Array.isArray(mv?.dischargeReview?.items) ? mv.dischargeReview.items : [];
  const rawDSItems = Array.isArray(ds?.itemsBreakdown) ? ds.itemsBreakdown : [];
  const itemsSource = rawMVItems.length > 0 ? 'mv' : (rawDSItems.length > 0 ? 'ds' : 'none');
  const items = (itemsSource === 'mv') ? rawMVItems : (itemsSource === 'ds' ? rawDSItems : []);
  const normalizeItem = (it) => {
    const name = it.itemName || it.name || it.title || 'Item';
    // Support both DischargeReview items and DischargeSettlement itemsBreakdown schema
    const billed = Number(
      it.billedAmount ?? it.billed ?? it.claimed ?? it.amount ?? 0
    );
    const eligible = Number(
      // Prefer finalPayable from computed breakdown, else explicit eligibleAmount
      it.finalPayable ?? it.eligibleAmount ?? it.approved ?? it.net ?? it.admissible ?? 0
    );
    const deduction = Number(
      it.deductionAmount ?? it.deduction ?? (billed - eligible)
    );
    const net = Number(it.netAmount ?? it.net ?? eligible);
    return { name, billed, eligible, deduction, net };
  };
  const normItems = items.map(normalizeItem);
  const totals = normItems.reduce((a, x) => ({
    billed: a.billed + x.billed,
    eligible: a.eligible + x.eligible,
    deduction: a.deduction + x.deduction,
    net: a.net + x.net,
  }), { billed: 0, eligible: 0, deduction: 0, net: 0 });

  // Use only the calculated finalApprovedAmount - no fallbacks
  const approvedByDoctor = Number(mv.finalApprovedAmount || 0);

  // Get copay and sublimit information from policy verification
  const coPayStructure = pv.coPayStructure || {};
  const deductiblesAndSublimits = pv.deductiblesAndSublimits || {};
  
  // Calculate copay amount from FAL computation if available
  const falComputation = app.workflow?.dischargeSettlement?.falComputation;
  const copayAmount = falComputation?.totals?.copay || 0;
  const sublimitAmount = falComputation?.totals?.sublimits || 0;
  
  // Fallback copay calculation
  let fallbackCopayAmount = 0;
  if (!copayAmount && coPayStructure.generalCoPayApplicable) {
    const copayPercent = parseFloat(coPayStructure.generalCoPayValue) || 0;
    if (copayPercent > 0) {
      fallbackCopayAmount = (approvedByDoctor * copayPercent) / 100;
    }
  }
  
  const displayCopayAmount = copayAmount || fallbackCopayAmount;

  const totalDeductions = Array.isArray(ds.deductions)
    ? ds.deductions.reduce((a, d) => a + (d?.percentage ? ((approvedByDoctor * (parseFloat(d.amount) || 0)) / 100) : (parseFloat(d.amount) || 0)), 0)
    : 0;
  const totalAdjustments = Array.isArray(ds.adjustments)
    ? ds.adjustments.reduce((a, x) => a + (parseFloat(x.amount) || 0), 0)
    : 0;
  
  const finalSettlement = Number(ds.finalSettlementAmount ?? (approvedByDoctor - totalDeductions + totalAdjustments));
  const patientPayable = Math.max(totals.billed - finalSettlement, 0);

  // Full patient name resolution (mirrors KYP logic)
  const getPatientDisplayName = (app) => {
    const candidates = [
      app?.patient?.fullName,
      app?.patient?.originalName,
      app?.patient?.completeName,
      app?.patient?.displayName,
      app?.patient?.legalName,
      app?.patient?.name,
    ].filter(v => typeof v === 'string' && v.trim().length > 0);
    if (candidates.length === 0) return 'Patient';
    const longest = candidates.reduce((a,b) => (b.trim().length > a.trim().length ? b : a));
    return normalizeText(longest);
  };

  return (
    <Document>
      {/* Page 1 - Main FAL Summary (mirrors KYP layout) */}
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.logoBox}>
            <Image src={samhealthLogo} style={{ width: 96, height: 52 }} />
          </View>
          <View style={styles.logoBox}>
            {lg?.documents?.entityLogo?.url ? (
              <Image src={lg.documents.entityLogo.url} style={{ width: 74, height: 44 }} />
            ) : (
              <Text style={styles.logoText}>{lg?.entityName || app?.leadGenerator?.entityName || 'LEAD GENERATOR'}</Text>
            )}
          </View>
        </View>

        {/* Title Bar */}
        <Text style={styles.titleBar}>FINAL APPROVAL LETTER (FAL)</Text>

    {/* Basic Info Row */}
        <View style={styles.twoColumn}>
          <View style={styles.leftColumn}>
            {/* Business Rule (2025-09-29): Subject MUST reference PATIENT name, never policy holder if different */}
            <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Subject:</Text><Text style={styles.fieldValue}>Final Approval for "{getPatientDisplayName(app)}"</Text></View>
            <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Dated:</Text><Text style={styles.fieldValue}>{formatDate(new Date())}</Text></View>
      <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Case ID:</Text><Text style={styles.fieldValue}>{app?.caseId || app?.applicationId || '—'}</Text></View>
          </View>
          <View style={styles.rightColumn}>
            {app?.documentType && (
              <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Document Type:</Text><Text style={styles.fieldValue}>{app.documentType}</Text></View>
            )}
            {pv?.intimationDetails?.intimationId && (
              <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Intimation ID:</Text><Text style={styles.fieldValue}>{pv.intimationDetails.intimationId}</Text></View>
            )}
            {app?.medical?.dischargeDate && (
              <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Discharge Date:</Text><Text style={styles.fieldValue}>{formatDate(app.medical.dischargeDate)}</Text></View>
            )}
          </View>
        </View>

        {/* Patient Details */}
        <Text style={styles.sectionHeader}>PATIENT DETAILS</Text>
        <View style={styles.table}>
          <View style={styles.tableRow}>
            <Text style={styles.tableColLabel}>Patient Name</Text>
            <Text style={styles.tableColValue}>{getPatientDisplayName(app)}</Text>
            { /* Always show Primary Insured separately ONLY if different from patient; else repeat patient name for clarity */ }
            <Text style={styles.tableColLabel}>Primary Insured (PI)</Text>
            <Text style={styles.tableColValue}>{
              (() => {
                const patientName = getPatientDisplayName(app);
                const piName = normalizeText(app?.policyHolder?.name || app?.policy?.primaryInsuredName || patientName);
                return piName === patientName ? patientName : piName; // ensures patient name not overridden incorrectly
              })()
            }</Text>
          </View>
          <View style={styles.tableRow}>
            { (app?.patient?.age || app?.patient?.gender) && (
              <>
                <Text style={styles.tableColLabel}>Age / Gender</Text>
                <Text style={styles.tableColValue}>{app?.patient?.age ?? '—'} / {app?.patient?.gender || '—'}</Text>
              </>
            )}
            { (app?.policyHolder?.isSelf || app?.patient?.relationship) && (
              <>
                <Text style={styles.tableColLabel}>Relationship to PI</Text>
                <Text style={styles.tableColValue}>{app?.policyHolder?.isSelf ? 'Self' : (app?.patient?.relationship || '—')}</Text>
              </>
            )}
          </View>
          <View style={styles.tableRow}>
            {app?.patient?.phone && (
              <>
                <Text style={styles.tableColLabel}>Contact</Text>
                <Text style={styles.tableColValue}>{app.patient.phone}</Text>
              </>
            )}
            {app?.patient?.email && (
              <>
                <Text style={styles.tableColLabel}>E-Mail ID</Text>
                <Text style={styles.tableColValue}>{app.patient.email}</Text>
              </>
            )}
          </View>
        </View>

        {/* Admission Details */}
        <Text style={styles.sectionHeader}>ADMISSION DETAILS</Text>
        <View style={styles.table}>
          <View style={styles.tableRow}>
            {app?.medical?.diagnosis && (
              <>
                <Text style={styles.tableColLabel}>Diagnosis</Text>
                <Text style={styles.tableColValue}>{app.medical.diagnosis}</Text>
              </>
            )}
            {app?.medical?.admissionDate && (
              <>
                <Text style={styles.tableColLabel}>Date of Admission</Text>
                <Text style={styles.tableColValue}>{fmtDate(app.medical.admissionDate)}</Text>
              </>
            )}
          </View>
          <View style={styles.tableRow}>
            {app?.medical?.treatmentType && (
              <>
                <Text style={styles.tableColLabel}>Treatment Type</Text>
                <Text style={styles.tableColValue}>{normalizeText(app.medical.treatmentType.toUpperCase())}</Text>
              </>
            )}
            {app?.medical?.dischargeDate && (
              <>
                <Text style={styles.tableColLabel}>Date of Discharge</Text>
                <Text style={styles.tableColValue}>{fmtDate(app.medical.dischargeDate)}</Text>
              </>
            )}
          </View>
          <View style={styles.tableRow}>
            {app?.medical?.procedureName && (
              <>
                <Text style={styles.tableColLabel}>Procedure</Text>
                <Text style={styles.tableColValue}>{app.medical.procedureName}</Text>
              </>
            )}
            {app?.medical?.roomCategory && (
              <>
                <Text style={styles.tableColLabel}>Room Category</Text>
                <Text style={styles.tableColValue}>{app.medical.roomCategory}</Text>
              </>
            )}
          </View>
        </View>

        {/* Policy Details */}
        <Text style={styles.sectionHeader}>POLICY DETAILS</Text>
        <View style={styles.table}>
          <View style={styles.tableRow}>
            {app?.policy?.insuranceCompany && (
              <>
                <Text style={styles.tableColLabel}>Insurer (IC)</Text>
                <Text style={styles.tableColValue}>{app.policy.insuranceCompany}</Text>
              </>
            )}
            {app?.policy?.policyType && (
              <>
                <Text style={styles.tableColLabel}>Policy Type</Text>
                <Text style={styles.tableColValue}>{app.policy.policyType}</Text>
              </>
            )}
          </View>
          <View style={styles.tableRow}>
            {app?.policy?.tpaName && (
              <>
                <Text style={styles.tableColLabel}>TPA</Text>
                <Text style={styles.tableColValue}>{app.policy.tpaName}</Text>
              </>
            )}
            {app?.policy?.number && (
              <>
                <Text style={styles.tableColLabel}>Policy Number</Text>
                <Text style={styles.tableColValue}>{app.policy.number}</Text>
              </>
            )}
          </View>
          <View style={styles.tableRow}>
            {app?.policy?.validFrom && app?.policy?.validTo && (
              <>
                <Text style={styles.tableColLabel}>Policy Period</Text>
                <Text style={styles.tableColValue}>{`${fmtDate(app.policy.validFrom)} to ${fmtDate(app.policy.validTo)}`}</Text>
              </>
            )}
            {app?.policy?.sumInsured && (
              <>
                <Text style={styles.tableColLabel}>Sum Insured</Text>
                <Text style={styles.tableColValue}>{formatINR(app.policy.sumInsured)}</Text>
              </>
            )}
          </View>
        </View>

        {/* Enhanced Coverage Analysis (from KYP) */}
        <View style={styles.coverageSection}>
          <Text style={styles.coverageHeader}>COVERAGE BREAKDOWN & ELIGIBILITY</Text>
          <View style={styles.coverageItem}>
            <Text style={styles.coverageLabel}>Base Sum Insured:</Text>
            <Text style={styles.coverageValue}>{formatINR(coverage?.baseCoverage || app?.policy?.sumInsured)}</Text>
          </View>
          <View style={styles.coverageItem}>
            <Text style={styles.coverageLabel}>Cumulative Bonus:</Text>
            <Text style={styles.coverageValue}>{formatINR(coverage?.cumulativeBonus)}</Text>
          </View>
          <View style={styles.coverageItem}>
            <Text style={styles.coverageLabel}>Top-up Sum Insured:</Text>
            <Text style={styles.coverageValue}>{formatINR(coverage?.topUpCoverage)}</Text>
          </View>
          <View style={styles.coverageItem}>
            <Text style={styles.coverageLabel}>Effective Total Coverage:</Text>
            <Text style={styles.coverageValue}>{formatINR(coverage?.effectiveTotalSumInsured)}</Text>
          </View>
          <View style={styles.coverageItem}>
            <Text style={styles.coverageLabel}>Utilized Current Year:</Text>
            <Text style={styles.coverageValue}>{formatINR(coverage?.utilized)}</Text>
          </View>
          <View style={styles.coverageItem}>
            <Text style={styles.coverageLabel}>Balance Available:</Text>
            <Text style={styles.coverageValue}>{formatINR(coverage?.balanceSumInsured)}</Text>
          </View>
        </View>

        {/* Room & ICU Eligibility (only render if any data exists) */}
        {(roomEligibility?.roomType || roomEligibility?.roomAmountPerDay || roomEligibility?.icuAmountPerDay) && (
          <>
            <Text style={styles.sectionHeader}>ROOM & ICU ELIGIBILITY</Text>
            <View style={styles.table}>
              <View style={styles.tableRow}>
                {roomEligibility?.roomType && (<><Text style={styles.tableColLabel}>Room Type Eligible</Text><Text style={styles.tableColValue}>{roomEligibility.roomType}</Text></>)}
                {roomEligibility?.roomAmountPerDay && (<><Text style={styles.tableColLabel}>Room Amount/Day</Text><Text style={styles.tableColValue}>{formatINR(roomEligibility.roomAmountPerDay)}</Text></>)}
              </View>
              {(roomEligibility?.icuAmountPerDay) && (
                <View style={styles.tableRow}>
                  <Text style={styles.tableColLabel}>ICU Amount/Day</Text>
                  <Text style={styles.tableColValue}>{formatINR(roomEligibility.icuAmountPerDay)}</Text>
                  <Text style={styles.tableColLabel}></Text>
                  <Text style={styles.tableColValue}></Text>
                </View>
              )}
            </View>
          </>
        )}

        {/* Hospital Details */}
        <Text style={styles.sectionHeader}>HOSPITAL DETAILS</Text>
        <View style={styles.twoColumn}>
          <View style={styles.leftColumn}>
            <View style={{ width: 50, height: 30, borderWidth: 1, borderStyle: 'solid', borderColor: '#000', alignItems: 'center', justifyContent: 'center', marginBottom: 4 }}>
              <Text style={styles.logoText}>LOGO</Text>
            </View>
            <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Hospital Name:</Text><Text style={styles.fieldValue}>{app?.hospitalName || app?.hospital?.name || ''}</Text></View>
          </View>
          <View style={styles.rightColumn}>
            <View style={styles.fieldRow}><Text style={styles.fieldLabel}>City:</Text><Text style={styles.fieldValue}>{app?.hospital?.city || app?.leadGenerator?.city || ''}</Text></View>
          </View>
        </View>

        {/* Medical Information */}
        <Text style={styles.sectionHeader}>Medical Information</Text>
        <View style={styles.twoColumn}>
          <View style={styles.leftColumn}>
            <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Diagnosis:</Text><Text style={styles.fieldValue}>{safeRender(app?.medical?.diagnosis || app?.medical?.treatments?.[0]?.diagnosisName)}</Text></View>
            <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Procedure Name:</Text><Text style={styles.fieldValue}>{safeRender(app?.medical?.procedureName || app?.medical?.treatments?.[0]?.procedureName)}</Text></View>
            <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Date of Admission:</Text><Text style={styles.fieldValue}>{formatDate(app?.medical?.admissionDate)}</Text></View>
          </View>
          <View style={styles.rightColumn}>
            <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Treatment Type:</Text><Text style={styles.fieldValue}>{safeRender(app?.medical?.treatmentType)}</Text></View>
            <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Discharge Date:</Text><Text style={styles.fieldValue}>{formatDate(app?.medical?.dischargeDate)}</Text></View>
          </View>
        </View>

        {/* Treatment Details (table mirroring KYP) */}
        {Array.isArray(app?.medical?.treatments) && app.medical.treatments.length > 0 && (
          <>
            <Text style={styles.sectionHeader}>Treatment Details</Text>
            <View style={styles.contentBox}>
              <View style={{ borderWidth: 1, borderStyle: 'solid', borderColor: '#000' }}>
                {/* Header row */}
                <View style={{ flexDirection: 'row', backgroundColor: '#e5e7eb', borderBottomWidth: 1, borderBottomColor: '#000', borderBottomStyle: 'solid' }}>
                  <Text style={[styles.fieldLabel, { width: '28%', fontWeight: 'bold' }]}>Diagnosis</Text>
                  <Text style={[styles.fieldLabel, { width: '24%', fontWeight: 'bold' }]}>Procedure</Text>
                  <Text style={[styles.fieldLabel, { width: '18%', fontWeight: 'bold' }]}>ICD Code</Text>
                  <Text style={[styles.fieldLabel, { width: '30%', fontWeight: 'bold' }]}>Medical Description</Text>
                </View>
                {/* Data rows */}
                {app.medical.treatments.map((t, idx) => (
                  <View key={idx} style={{ flexDirection: 'row', borderBottomWidth: idx === app.medical.treatments.length - 1 ? 0 : 1, borderBottomColor: '#d1d5db', borderBottomStyle: 'solid' }}>
                    <Text style={[styles.fieldValue, { width: '28%', fontWeight: 'normal' }]}>{safeRender(t?.diagnosisName || app?.medical?.diagnosis)}</Text>
                    <Text style={[styles.fieldValue, { width: '24%', fontWeight: 'normal' }]}>{safeRender(t?.procedureName || app?.medical?.procedureName)}</Text>
                    <Text style={[styles.fieldValue, { width: '18%', fontWeight: 'normal' }]}>{safeRender(t?.icdCode || app?.medical?.icdCode)}</Text>
                    <Text style={[styles.fieldValue, { width: '30%', fontWeight: 'normal' }]}>{safeRender(t?.description || t?.clinicalDescription || t?.notes || app?.medical?.description)}</Text>
                  </View>
                ))}
              </View>
            </View>
          </>
        )}

        {/* Footer */}
  <Text style={styles.footer}>This is a system-generated document. For verification, quote Case ID</Text>
      </Page>

      {/* Page 2 - Coverage & Authorization Summary */}
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.logoBox}><Image src={samhealthLogo} style={{ width: 96, height: 52 }} /></View>
          <View style={styles.logoBox}>
            {lg?.documents?.entityLogo?.url ? (
              <Image src={lg.documents.entityLogo.url} style={{ width: 74, height: 44 }} />
            ) : (
              <Text style={styles.logoText}>{lg?.entityName || app?.leadGenerator?.entityName || 'LEAD GENERATOR'}</Text>
            )}
          </View>
        </View>

  {/* Coverage Information and Eligibility */}
  <Text style={styles.sectionHeader}>Coverage Information and Eligibility</Text>
        <View style={styles.twoColumn}>
          <View style={styles.leftColumn}>
            <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Sum Insured (Base):</Text><Text style={styles.fieldValue}>{formatINR(coverage?.baseCoverage)}</Text></View>
            <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Top-up Sum Insured:</Text><Text style={styles.fieldValue}>{formatINR(coverage?.topUpCoverage)}</Text></View>
            <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Sum Insured Utilised (YTD):</Text><Text style={styles.fieldValue}>{formatINR(coverage?.utilized)}</Text></View>
            {roomEligibility?.roomAmountPerDay && (
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Eligible Room (Amount/Day):</Text>
                <Text style={styles.fieldValue}>{`${formatINR(roomEligibility.roomAmountPerDay)} per day`}</Text>
              </View>
            )}
            {roomEligibility?.icuAmountPerDay && (
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Eligible ICU (Amount/Day):</Text>
                <Text style={styles.fieldValue}>{`${formatINR(roomEligibility.icuAmountPerDay)} per day`}</Text>
              </View>
            )}
          </View>
          <View style={styles.rightColumn}>
            <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Cumulative Bonus:</Text><Text style={styles.fieldValue}>{formatINR(coverage?.cumulativeBonus)}</Text></View>
            <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Effective Sum Insured:</Text><Text style={styles.fieldValue}>{formatINR(coverage?.effectiveTotalSumInsured)}</Text></View>
            <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Balance Sum Insured:</Text><Text style={styles.fieldValue}>{formatINR(coverage?.balanceSumInsured)}</Text></View>
            {pv?.coverageBreakdown?.ailmentSublimit && (
              <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Ailment/Procedure Sublimit:</Text><Text style={styles.fieldValue}>{formatINR(pv.coverageBreakdown.ailmentSublimit)}</Text></View>
            )}
          </View>
        </View>

        {/* Financial Summary (new) */}
        <Text style={styles.sectionHeader}>FINANCIAL SUMMARY</Text>
        <View style={styles.contentBox}>
          <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Item Deductions (Calculated):</Text><Text style={styles.fieldValue}>{formatINR(totals.deduction)}</Text></View>
          {(totalDeductions > 0) && (
            <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Additional Deductions:</Text><Text style={styles.fieldValue}>{formatINR(totalDeductions)}</Text></View>
          )}
          {(displayCopayAmount > 0) && (
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Co-pay Deduction:</Text>
              <Text style={styles.fieldValue}>
                {formatINR(displayCopayAmount)}
                {coPayStructure.generalCoPayApplicable && ` (${coPayStructure.generalCoPayValue || 0}%)`}
              </Text>
            </View>
          )}
          {(sublimitAmount > 0) && (
            <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Sublimit Deduction:</Text><Text style={styles.fieldValue}>{formatINR(sublimitAmount)}</Text></View>
          )}
          {(totalAdjustments !== 0) && (
            <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Adjustments:</Text><Text style={styles.fieldValue}>{formatINR(totalAdjustments)}</Text></View>
          )}
          {approvedByDoctor !== finalSettlement && (
            <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Doctor Approved (Pre-Settlement):</Text><Text style={styles.fieldValue}>{formatINR(approvedByDoctor)}</Text></View>
          )}
          <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Variance (Billed - Final):</Text><Text style={styles.fieldValue}>{formatINR(totals.billed - finalSettlement)}</Text></View>
          {/* Highlight Key Figures */}
          <View style={styles.highlightBox}>
            <View style={styles.highlightRow}>
              <Text style={styles.highlightLabel}>Total Billed</Text>
              <Text style={styles.highlightValue}>{formatINR(totals.billed)}</Text>
            </View>
            <View style={styles.highlightRow}>
              <Text style={styles.highlightLabel}>Approved / Final Settlement</Text>
              <Text style={styles.highlightValue}>{formatINR(finalSettlement)}</Text>
            </View>
            <View style={[styles.highlightRow, { borderTopWidth: 1, borderTopColor: '#1e3a8a', borderTopStyle: 'solid', paddingTop: 6, marginTop: 4 }]}> 
              <Text style={styles.highlightLabel}>PATIENT PAYABLE</Text>
              <Text style={styles.highlightPayable}>{formatINR(patientPayable)}</Text>
            </View>
          </View>
        </View>

        {/* Authorization & Settlement Summary */}
        <Text style={styles.sectionHeader}>Authorization & Settlement Summary</Text>
        <View style={styles.contentBox}>
          <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Medical History Verified:</Text><Text style={styles.fieldValue}>Yes</Text></View>
          <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Documents Authentic:</Text><Text style={styles.fieldValue}>Yes</Text></View>
          <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Treatment Appropriate to Diagnosis:</Text><Text style={styles.fieldValue}>Yes</Text></View>
          <View style={[styles.fieldRow, { marginTop: 4 }]}><Text style={styles.fieldLabel}>Doctor Approved Amount:</Text><Text style={styles.fieldValue}>{formatINR(approvedByDoctor)}</Text></View>
          <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Total Deductions:</Text><Text style={styles.fieldValue}>{formatINR(totalDeductions)}</Text></View>
          {(displayCopayAmount > 0) && (
            <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Co-pay Amount:</Text><Text style={styles.fieldValue}>{formatINR(displayCopayAmount)}</Text></View>
          )}
          {(sublimitAmount > 0) && (
            <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Sublimit Amount:</Text><Text style={styles.fieldValue}>{formatINR(sublimitAmount)}</Text></View>
          )}
          <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Total Adjustments:</Text><Text style={styles.fieldValue}>{formatINR(totalAdjustments)}</Text></View>
          <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Final Settlement:</Text><Text style={styles.fieldValue}>{formatINR(finalSettlement)}</Text></View>
        </View>

        {/* Instructions */}
        <Text style={styles.sectionHeader}>Instructions</Text>
        <View style={styles.contentBox}>
          <Text style={{ fontSize: 10, lineHeight: 1.3 }}>
            The settlement is approved based on the discharge summary and bills submitted. Final liability is subject to verification of all originals and policy terms. Room upgrades and non-payable items shall be deducted as per policy. Expenses not payable under IRDAI guidelines will be excluded.
          </Text>
        </View>

        {/* Footer */}
  <Text style={styles.footer}>This is a system-generated document. For verification, quote Case ID</Text>
      </Page>

      {/* Page 3 - Discharge Items & Signatures */}
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.logoBox}><Image src={samhealthLogo} style={{ width: 96, height: 52 }} /></View>
          <View style={styles.logoBox}>
            {lg?.documents?.entityLogo?.url ? (
              <Image src={lg.documents.entityLogo.url} style={{ width: 74, height: 44 }} />
            ) : (
              <Text style={styles.logoText}>{lg?.entityName || app?.leadGenerator?.entityName || 'LEAD GENERATOR'}</Text>
            )}
          </View>
        </View>

        {/* Discharge Items Verification (parity with Discharge Summary Review table in app) */}
        <Text style={styles.sectionHeader}>Discharge Items Verification</Text>
        <View style={styles.table}>
          <View style={styles.tr}>
            <Text style={[styles.th, { flex: 2 }]}>Item/Service</Text>
            <Text style={[styles.th, { flex: 1 }]}>Category</Text>
            <Text style={[styles.th, styles.tdRight, { flex: 1 }]}>Rate/Unit</Text>
            <Text style={[styles.th, styles.tdRight, { flex: 1 }]}>Elig. Rate/Unit</Text>
            <Text style={[styles.th, { flex: 0.7, textAlign: 'center' }]}>Qty</Text>
            <Text style={[styles.th, styles.tdRight, { flex: 1 }]}>Billed Amount</Text>
            <Text style={[styles.th, styles.tdRight, { flex: 1 }]}>Eligible Amount</Text>
            <Text style={[styles.th, styles.tdRight, { flex: 1 }]}>Variance</Text>
            <Text style={[styles.th, { flex: 1.5 }]}>Remarks</Text>
            <Text style={[styles.th, { flex: 0.8, textAlign: 'center' }]}>Approved</Text>
          </View>
          {Array.isArray(items) && items.length > 0 ? (
            <>
              {items.map((raw, idx) => {
                const name = raw.itemName || raw.name || raw.title || 'Item';
                const category = raw.category || raw.type || '-';
                const qty = Number(raw.quantity || raw.qty || 0);
                const unitRate = Number(raw.unitRate || raw.rate || raw.unit || 0);
                const eligibleUnitRate = Number(raw.eligibleUnitRate ?? raw.eligibleRate ?? unitRate ?? 0);
                const billed = Number((raw.billedAmount ?? raw.billed ?? (unitRate * qty)) ?? 0);
                const eligible = Number((raw.finalPayable ?? raw.eligibleAmount ?? (eligibleUnitRate * qty)) ?? 0);
                const variance = billed - eligible;
                const remarksCell = (raw.remarks ?? raw.comment ?? '-').toString();
                const approved = raw.approved === true || raw.status === 'approved' || raw.isApproved === true;
                return (
                  <View key={idx} style={styles.tr}>
                    <Text style={[styles.td, { flex: 2 }]}>{name}</Text>
                    <Text style={[styles.td, { flex: 1 }]}>{category}</Text>
                    <Text style={[styles.td, styles.tdRight, { flex: 1 }]}>{formatINR(unitRate)}</Text>
                    <Text style={[styles.td, styles.tdRight, { flex: 1 }]}>{formatINR(eligibleUnitRate)}</Text>
                    <Text style={[styles.td, { flex: 0.7, textAlign: 'center' }]}>{qty}</Text>
                    <Text style={[styles.td, styles.tdRight, { flex: 1 }]}>{formatINR(billed)}</Text>
                    <Text style={[styles.td, styles.tdRight, { flex: 1 }]}>{formatINR(eligible)}</Text>
                    <Text style={[styles.td, styles.tdRight, { flex: 1, color: variance > 0 ? 'red' : '#000' }]}>{formatINR(variance)}</Text>
                    <Text style={[styles.td, { flex: 1.5 }]}>{remarksCell}</Text>
                    <Text style={[styles.td, { flex: 0.8, textAlign: 'center' }]}>{approved ? 'Yes' : 'No'}</Text>
                  </View>
                );
              })}
              {/* Totals Row */}
              {(() => {
                const totalBilled = items.reduce((a, raw) => a + Number((raw.billedAmount ?? raw.billed ?? (Number(raw.unitRate || raw.unit || 0) * Number(raw.quantity || 0))) ?? 0), 0);
                const totalEligible = items.reduce((a, raw) => {
                  const qty = Number(raw.quantity || 0);
                  const unitRate = Number(raw.unitRate || raw.unit || 0);
                  const eligibleUnitRate = Number(raw.eligibleUnitRate ?? raw.eligibleRate ?? unitRate ?? 0);
                  const eligible = Number((raw.finalPayable ?? raw.eligibleAmount ?? (eligibleUnitRate * qty)) ?? 0);
                  return a + eligible;
                }, 0);
                const totalVariance = totalBilled - totalEligible;
                return (
                  <View style={styles.tr}>
                    <Text style={[styles.td, { flex: 2, fontWeight: 'bold' }]}>Total</Text>
                    <Text style={[styles.td, { flex: 1 }]}></Text>
                    <Text style={[styles.td, { flex: 1 }]}></Text>
                    <Text style={[styles.td, { flex: 1 }]}></Text>
                    <Text style={[styles.td, { flex: 0.7 }]}></Text>
                    <Text style={[styles.td, styles.tdRight, { flex: 1, fontWeight: 'bold' }]}>{formatINR(totalBilled)}</Text>
                    <Text style={[styles.td, styles.tdRight, { flex: 1, fontWeight: 'bold' }]}>{formatINR(totalEligible)}</Text>
                    <Text style={[styles.td, styles.tdRight, { flex: 1, fontWeight: 'bold', color: totalVariance > 0 ? 'red' : '#000' }]}>{formatINR(totalVariance)}</Text>
                    <Text style={[styles.td, { flex: 1.5 }]}></Text>
                    <Text style={[styles.td, { flex: 0.8 }]}></Text>
                  </View>
                );
              })()}
            </>
          ) : (
            <View style={styles.tr}>
              <Text style={[styles.td, { flex: 2 }]}>No discharge items available</Text>
              <Text style={[styles.td, { flex: 1 }]}></Text>
              <Text style={[styles.td, { flex: 1 }]}></Text>
              <Text style={[styles.td, { flex: 1 }]}></Text>
              <Text style={[styles.td, { flex: 0.7 }]}></Text>
              <Text style={[styles.td, { flex: 1 }]}></Text>
              <Text style={[styles.td, { flex: 1 }]}></Text>
              <Text style={[styles.td, { flex: 1 }]}></Text>
              <Text style={[styles.td, { flex: 1.5 }]}></Text>
              <Text style={[styles.td, { flex: 0.8 }]}></Text>
            </View>
          )}
        </View>

        {/* Notes */}
        <Text style={styles.sectionHeader}>Notes</Text>
        <View style={styles.contentBox}>
          <Text style={{ fontSize: 10, lineHeight: 1.3 }}>{aggregateRemarks(application, remarks)}</Text>
        </View>

        {/* Final Verification Checklist (KYP-style) */}
        <Text style={styles.sectionHeader}>Final Verification Checklist</Text>
        <View style={[styles.contentBox, { padding: 8, backgroundColor: '#f9f9f9' }]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginVertical: 3 }}>
            <Text style={{ color: '#00a651', fontWeight: 'bold', marginRight: 8, fontSize: 12 }}>✅</Text>
            <Text style={{ fontSize: 10, flex: 1 }}>Medical documentation review completed</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginVertical: 3 }}>
            <Text style={{ color: '#00a651', fontWeight: 'bold', marginRight: 8, fontSize: 12 }}>✅</Text>
            <Text style={{ fontSize: 10, flex: 1 }}>Policy coverage verification completed</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginVertical: 3 }}>
            <Text style={{ color: '#00a651', fontWeight: 'bold', marginRight: 8, fontSize: 12 }}>✅</Text>
            <Text style={{ fontSize: 10, flex: 1 }}>Discharge summary and bills verified</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginVertical: 3 }}>
            <Text style={{ color: '#00a651', fontWeight: 'bold', marginRight: 8, fontSize: 12 }}>✅</Text>
            <Text style={{ fontSize: 10, flex: 1 }}>Co-payment and deductibles calculated</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginVertical: 3 }}>
            <Text style={{ color: '#00a651', fontWeight: 'bold', marginRight: 8, fontSize: 12 }}>✅</Text>
            <Text style={{ fontSize: 10, flex: 1 }}>Final settlement amount approved</Text>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginVertical: 3 }}>
            <Text style={{ color: '#00a651', fontWeight: 'bold', marginRight: 8, fontSize: 12 }}>✅</Text>
            <Text style={{ fontSize: 10, flex: 1 }}>Authorization cleared for payment</Text>
          </View>
        </View>

        {/* Signatures */}
        <Text style={styles.sectionHeader}>Authorization</Text>
        <View style={[styles.contentBox, { padding: 8 }]}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <View style={{ width: '30%' }}>
              <View style={{ borderTopWidth: 1, borderColor: '#000', marginTop: 24, paddingTop: 4 }} />
              <Text style={{ fontSize: 9, textAlign: 'center' }}>Authorized Signatory</Text>
            </View>
            <View style={{ width: '30%' }}>
              <View style={{ borderTopWidth: 1, borderColor: '#000', marginTop: 24, paddingTop: 4 }} />
              <Text style={{ fontSize: 9, textAlign: 'center' }}>Hospital Representative</Text>
            </View>
            <View style={{ width: '30%' }}>
              <View style={{ borderTopWidth: 1, borderColor: '#000', marginTop: 24, paddingTop: 4 }} />
              <Text style={{ fontSize: 9, textAlign: 'center' }}>For Samriddhi Healthcare</Text>
            </View>
          </View>
        </View>

  {/* Footer */}
  <Text style={styles.footer}>This is a system-generated document. For verification, quote Case ID</Text>
      </Page>

      {/* Page 4 - Terms and Conditions (same content parity as KYP) */}
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.logoBox}><Image src={samhealthLogo} style={{ width: 96, height: 52 }} /></View>
          <View style={styles.logoBox}>
            {lg?.documents?.entityLogo?.url ? (
              <Image src={lg.documents.entityLogo.url} style={{ width: 74, height: 44 }} />
            ) : (
              <Text style={styles.logoText}>{lg?.entityName || app?.leadGenerator?.entityName || 'LEAD GENERATOR'}</Text>
            )}
          </View>
        </View>

        {/* Approval Terms and Conditions */}
        <View style={{ borderWidth: 1, borderStyle: 'solid', borderColor: '#000', padding: 8, marginBottom: 10 }}>
          <Text style={{ fontSize: 11, fontWeight: 'bold', marginBottom: 6 }}>Approval terms and Conditions (Final Approval & Discharge Settlement)</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.4, marginBottom: 8 }}>
            This approval confirms the final settlement eligibility based on policy terms and the documents submitted. It does not guarantee payment of items excluded by policy/IRDAI norms and is subject to verification of originals.
          </Text>
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>1. Policy Governance</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 6 }}>Subject to co-payments, deductibles, sub-limits, and Reasonable & Customary Charges. Upgrades in room category may attract proportionate deductions.</Text>
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>2. Documentation Requirement</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 6 }}>Final bill, discharge summary, diagnostics, and all relevant documents must be submitted for settlement.</Text>
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>3. Validity & Integrity</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 6 }}>Any misrepresentation, suppression, or concealment of facts shall render this approval null and void.</Text>
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>4. Coverage Limits</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 6 }}>Reimbursable only up to R&C/UCR applicable to the geographical area. Excess billing beyond standard charges shall not be payable.</Text>
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>5. Basis of Assessment</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 6 }}>Assessment is based on KYP verification, discharge documents and policy eligibility as decoded from the insurance policy.</Text>
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>6. Non-Payable Expenses</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 4 }}>As per IRDAI's Standard Exclusion List, non-medical expenses are not payable, including:</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 6, paddingLeft: 10 }}>
            • Admission/registration/documentation charges{"\n"}
            • Diet charges beyond prescription{"\n"}
            • Cosmetic/aesthetic procedures{"\n"}
            • Assistant surgeon fee not justified{"\n"}
            • Routine physiotherapy, nutritional supplements, energy drinks{"\n"}
            • Personal comfort items (telephone, attendant meals, laundry, TV, toiletries, etc.){"\n"}
            • Non-medical consumables excluded under IRDAI's standard non-payable list.
          </Text>
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>7. Escalation of Costs & LOS</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 6 }}>Any increase in expense/length of stay requires justification with medical records for re-evaluation.</Text>
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>8. Exclusions</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 6 }}>Standard policy exclusions apply (e.g., cosmetic, dental, experimental treatments, self-inflicted injuries, intoxication, etc.).</Text>
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>9. Modern Treatments</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 6 }}>If covered, payable only as per policy-specific caps and sub-limits; unproven/experimental procedures are excluded.</Text>
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>10. Liability</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3 }}>If insurer reduces/rejects due to false/incorrect documents or information, liability to settle balance remains with the insured/hospital.</Text>
        </View>

  {/* Footer */}
  <Text style={styles.footer}>This is a system-generated document. For verification, quote Case ID</Text>
      </Page>
    </Document>
  );
};

export default ComprehensiveFALDocument;
